import { MessageBus } from '@23degrees/shared/bus';
import { BaseClass } from '@23degrees/shared/classes/base.class';

import { AuthQuery } from '@23degrees/stores/auth/auth.query';
import { UiStore } from '@23degrees/stores/ui/ui.store';
import { Injectable, Injector, REQUEST, effect, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MESSAGE_RECIPIENTS, RESPONSE_MESSAGES } from '@config';
import { TranslateService } from '@ngx-translate/core';
import get from 'lodash/get';
import set from 'lodash/set';
import { BehaviorSubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
// Note for angular 5 you need to register the languages

@Injectable({
  providedIn: 'root',
})
export class LanguageService extends BaseClass {
  private messageBus = inject(MessageBus);
  private authQuery = inject(AuthQuery);
  private uiStore = inject(UiStore);
  private translate = inject(TranslateService);
  private request = inject(REQUEST, {optional: true})

  defaultLang: string = 'de';
  currentLang: string = 'de';
  currentLang$: BehaviorSubject<string> = new BehaviorSubject<string>(this.currentLang);


  constructor() {
    super();
    if (this.isBrowser()) {
      let lang1 = 'de';
      let lang2 = 'de';
      if (navigator?.language) {
        lang1 = navigator.language.split('-')[0];
      }
      if (navigator && (navigator as any).userLanguage) {
        lang2 = (navigator as any).userLanguage;
      }
      const localeLanguage = lang1 || lang2 || 'de';

      if (['de', 'en'].includes(localeLanguage)) {
        this.defaultLang = localeLanguage;
      } else {
        this.defaultLang = 'en';
      }
    }
    this.translate.addLangs(['en', 'de']);
    this.translate.setDefaultLang(this.defaultLang);
    this.globalService.document.documentElement.lang = this.defaultLang;

    // this.messageBus.process(
    //   REQUEST_MESSAGES.LanguageSwitch,
    //   (msg) => {
    //     this.setLanguage(msg);
    //     this.messageBus.emit(RESPONSE_MESSAGES.LanguageSwitch, [MESSAGE_RECIPIENTS.All], msg);
    //   },
    //   this.componentDestroyed$
    // );

    this.authQuery.language$.pipe(takeUntil(this.componentDestroyed$)).subscribe((lang) => this.updateLanguage(lang, true));

    effect(() => {
      const value = this.uiStore.uiLanguage$();
      if (value) {
        this.updateLanguage(value);
      }
    });
  }

  updateLanguage = (uiLanguage, ignoreAnonUser: boolean = false) => {
    if (ignoreAnonUser && this.authQuery.isAnonUser()) {
      uiLanguage = this.defaultLang;
    }
    // console.log('[LanguageService][updateLanguage]', { user: this.authQuery.getValue(), uiLanguage, defautl: this.defaultLang });
    this.currentLang = uiLanguage || this.defaultLang;
    this.setLanguage(this.currentLang);
  };

  setLanguage(lang) {
    // this.currentLang = lang;
    this.currentLang$.next(lang);
    this.translate.use(lang);
    this.globalService.document.documentElement.lang = lang;
    this.messageBus.emit(RESPONSE_MESSAGES.LanguageSwitch, [MESSAGE_RECIPIENTS.All], this.currentLang);
  }

  getLanguage() {
    return this.currentLang;
  }

  // Use this after load and everything is in place
  translateStringSync = (str: string, params?) => {
    const translatedString = this.translate.instant(str, params);
    // console.log('[LanguageService][translateStringSync]', { str, translatedString });
    return translatedString;
  };

  /**
   * Use this if your are not sure if the language is already fetched (init in page etc.)
   */
  translateStringAsync = async (str: string) => {
    // console.log('[LanguageService][translateStringAsync]', { str });
    if (str.length > 0) {
      const translatedString = await this.translate.get(str).toPromise();
      return translatedString;
    }
    return '';
  };

  translateObjectArray = async (objArr: Array<any>, path: string = '', paths?: Array<string>) => {
    objArr = JSON.parse(JSON.stringify(objArr));
    const translate = async (idx, currentPath) => {
      let item = get(objArr[idx], currentPath);
      if (item) {
        item = await this.translate.get(item).toPromise();
        set(objArr[idx], currentPath, item);
      }
    };

    for (let idx = 0; idx < objArr.length; idx++) {
      if (paths?.length > 0) {
        for (const currentPath of paths) {
          await translate(idx, currentPath);
        }
      } else {
        await translate(idx, path);
      }
    }
    return objArr;
  };

  translateObjectArraySync = (objArr: Array<any>, path: string = '', paths?: Array<string>) => {
    // for (let idx = 0; idx < objArr.length; idx++) {
    //   let item = get(objArr[idx], path);
    //   if (item) {
    //     item = this.translate.instant(item);
    //     set(objArr[idx], path, item);
    //   }
    // }
    // return objArr;
    objArr = JSON.parse(JSON.stringify(objArr));
    const translate = (idx, currentPath) => {
      let item = get(objArr[idx], currentPath);
      if (item) {
        item = this.translate.instant(item);
        set(objArr[idx], currentPath, item);
      }
    };

    for (let idx = 0; idx < objArr.length; idx++) {
      if (paths?.length > 0) {
        for (const currentPath of paths) {
          translate(idx, currentPath);
        }
      } else {
        translate(idx, path);
      }
    }
    return objArr;
  };

  translateObject = async (obj: any) => {
    // Variant A
    const props = Object.getOwnPropertyNames(obj);
    const returnObj = {};

    for (const [key, value] of Object.entries(obj)) {
      returnObj[key] = await this.translate.get(<any>value).toPromise();
    }
    // console.log('[LanguageService]', '[translateObject]', obj, returnObj);
    return returnObj;
  };

  translateObjectSync = (obj: any) => {
    // Variant B
    const props = Object.getOwnPropertyNames(obj);
    const returnObj = {};
    for (const [key, value] of Object.entries(obj)) {
      returnObj[key] = this.translate.instant(<any>value).toPromise();
    }
    // console.log('[LanguageService]', '[translateObjectSync]', obj, returnObj);
    return returnObj;
  };

  translateNestedObjectSync = (obj: any, path = '', paths?: Array<string>) => {
    obj = JSON.parse(JSON.stringify(obj));
    const translate = (prop, currentPath) => {
      let item = get(obj[prop], currentPath);
      if (item) {
        item = this.translate.instant(item);
        set(obj[prop], currentPath, item);
      }
    };

    for (const key of Object.keys(obj)) {
      if (paths && paths.length > 0) {
        for (const currentPath of paths) {
          translate(key, currentPath);
        }
      } else {
        translate(key, path);
      }
    }

    // const props = Object.getOwnPropertyNames(obj);
    // for (let idx = 0; idx < props.length; idx++) {
    //   if (paths && paths.length > 0) {
    //     for (const currentPath of paths) {
    //       translate(props[idx], currentPath);
    //     }
    //   } else {
    //     translate(props[idx], path);
    //   }
    // }

    return obj;
  };
}
